import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CancelPage() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('cancel'); // 'cancel' | 'reschedule'

  // Cancel state
  const [reasonCategory, setReasonCategory] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [submittingCancel, setSubmittingCancel] = useState(false);
  const [cancelDone, setCancelDone] = useState(false);

  // Reschedule state
  const [newDate, setNewDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [newTime, setNewTime] = useState('');
  const [submittingResched, setSubmittingResched] = useState(false);
  const [reschedDone, setReschedDone] = useState(null); // { auto_rescheduled, new_date, new_time, message }

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/cancel/${token}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setError(err.detail || 'Booking link is invalid or expired.');
        } else {
          const data = await res.json();
          setBooking(data);
        }
      } catch (e) {
        setError('Could not load your booking. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const lateFeeApplies = useMemo(() => booking?.late_fee_applies, [booking]);
  const isResolved = booking?.is_resolved || cancelDone || reschedDone?.auto_rescheduled;

  const loadSlots = async (date) => {
    if (!date || !booking) return;
    setLoadingSlots(true);
    setNewTime('');
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/bookings/available-slots?date=${dateStr}&package_name=${encodeURIComponent(
          booking.package?.package_name || ''
        )}`
      );
      const data = await res.json();
      setAvailableSlots(data.available_slots || []);
      if ((data.available_slots || []).length > 0) setNewTime(data.available_slots[0]);
    } catch (e) {
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateChange = (d) => {
    setNewDate(d);
    loadSlots(d);
  };

  const submitCancel = async (e) => {
    e.preventDefault();
    if (!reasonCategory) {
      alert('Please select a cancellation reason.');
      return;
    }
    setSubmittingCancel(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cancel/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason_category: reasonCategory, reason_text: reasonText }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Could not submit your request.');
        return;
      }
      setCancelDone(true);
    } catch (err) {
      alert('Network error. Please try again.');
    } finally {
      setSubmittingCancel(false);
    }
  };

  const submitReschedule = async (e) => {
    e.preventDefault();
    if (!newDate || !newTime) {
      alert('Please pick a new date and time.');
      return;
    }
    const y = newDate.getFullYear();
    const m = String(newDate.getMonth() + 1).padStart(2, '0');
    const d = String(newDate.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;

    setSubmittingResched(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/reschedule/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_date: dateStr, new_time: newTime }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || 'Could not reschedule.');
        return;
      }
      setReschedDone(data);
    } catch (err) {
      alert('Network error. Please try again.');
    } finally {
      setSubmittingResched(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-gray-500">Loading your booking...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-md p-8 max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Link Unavailable</h1>
          <p className="text-gray-600 mb-6" data-testid="cancel-error">{error}</p>
          <Link to="/" className="inline-block bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  if (!booking) return null;

  if (cancelDone) {
    return (
      <ResultCard
        title="Cancellation Request Received"
        body={
          lateFeeApplies
            ? "Thanks — we've received your cancellation request. Because it's within 24 hours of your booking, our team will be in touch to confirm the next steps. A $220 (inc GST) late cancellation fee may apply, unless waived (e.g. for weather)."
            : "Your booking has been cancelled. No fee has been charged. We've sent confirmation to your email."
        }
        testid="cancel-done"
      />
    );
  }

  if (reschedDone?.auto_rescheduled) {
    return (
      <ResultCard
        title="Booking Rescheduled"
        body={
          reschedDone.late_fee_applied
            ? `Your booking has been moved to ${reschedDone.new_date} at ${reschedDone.new_time}. Because the change was made within 24 hours of the original appointment, a $220 (inc GST) late reschedule fee will be added to your final invoice. A confirmation email is on its way.`
            : `Your booking has been moved to ${reschedDone.new_date} at ${reschedDone.new_time}. A confirmation email is on its way.`
        }
        testid="reschedule-done"
      />
    );
  }

  if (reschedDone && !reschedDone.auto_rescheduled) {
    return (
      <ResultCard
        title="Reschedule Request Received"
        body="Your team will review the request and confirm shortly."
        testid="reschedule-pending"
      />
    );
  }

  if (isResolved) {
    return (
      <ResultCard
        title="This booking has already been resolved"
        body="If you believe this is in error, please contact us at media@matthewminchio.com."
        testid="already-resolved"
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="bg-gradient-to-br from-teal-600 to-cyan-600 px-8 py-6 text-white">
            <h1 className="text-2xl font-bold">Manage Your Booking</h1>
            <p className="text-sm text-teal-50 mt-1">Cancel or reschedule your appointment below.</p>
          </div>

          <div className="p-8 space-y-6">
            {/* Booking summary */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
              <h2 className="font-semibold text-gray-900 mb-3">Booking Summary</h2>
              <dl className="space-y-2 text-sm">
                <Row label="Customer" value={booking.customer_name} />
                <Row label="Package" value={booking.package?.package_name} />
                <Row label="Property" value={booking.property_address || 'N/A'} />
                <Row label="Date & Time" value={`${booking.booking_date} at ${booking.booking_time}`} />
                <Row label="Total" value={`A$${Number(booking.package?.total_price || 0).toFixed(2)} incl GST`} />
              </dl>
            </div>

            {/* Late fee notice */}
            {lateFeeApplies && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900" data-testid="late-fee-notice">
                <strong>Heads up — within 24 hours of your booking.</strong>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li><span className="font-semibold">Cancel:</span> incurs a $220 (inc GST) fee, invoiced separately. Weather-related cancellations may be waived at our discretion.</li>
                  <li><span className="font-semibold">Reschedule:</span> the booking will be moved automatically and a $220 (inc GST) late reschedule fee will be added to your final invoice when the job is completed.</li>
                </ul>
              </div>
            )}

            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setTab('cancel')}
                className={`px-5 py-3 text-sm font-medium border-b-2 transition ${
                  tab === 'cancel'
                    ? 'border-teal-600 text-teal-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
                data-testid="tab-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => setTab('reschedule')}
                className={`px-5 py-3 text-sm font-medium border-b-2 transition ${
                  tab === 'reschedule'
                    ? 'border-teal-600 text-teal-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
                data-testid="tab-reschedule"
              >
                Reschedule
              </button>
            </div>

            {tab === 'cancel' ? (
              <form onSubmit={submitCancel} className="space-y-4" data-testid="cancel-form">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reason for cancellation *</label>
                  <select
                    value={reasonCategory}
                    onChange={(e) => setReasonCategory(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    required
                    data-testid="cancel-reason-select"
                  >
                    <option value="">Select a reason</option>
                    <option value="Weather">Weather</option>
                    <option value="Change of plans">Change of plans</option>
                    <option value="Property unavailable">Property unavailable</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional details (optional)</label>
                  <textarea
                    rows="3"
                    value={reasonText}
                    onChange={(e) => setReasonText(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Anything else we should know?"
                    data-testid="cancel-reason-text"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submittingCancel}
                  className="w-full px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50"
                  data-testid="cancel-submit"
                >
                  {submittingCancel ? 'Submitting...' : 'Request Cancellation'}
                </button>
              </form>
            ) : (
              <form onSubmit={submitReschedule} className="space-y-4" data-testid="reschedule-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">New Date *</label>
                    <DatePicker
                      selected={newDate}
                      onChange={handleDateChange}
                      minDate={new Date()}
                      filterDate={(date) => date.getDay() !== 0}
                      dateFormat="dd/MM/yyyy"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholderText="Choose a date"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Time * {loadingSlots && <span className="text-blue-600">(Loading...)</span>}
                    </label>
                    <select
                      value={newTime}
                      onChange={(e) => setNewTime(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      required
                      disabled={!newDate || loadingSlots}
                      data-testid="reschedule-time-select"
                    >
                      {!newDate && <option value="">Select date first</option>}
                      {newDate && availableSlots.length === 0 && !loadingSlots && (
                        <option value="">No slots available</option>
                      )}
                      {availableSlots.map((slot) => (
                        <option key={slot} value={slot}>
                          {slot}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submittingResched || !newDate || !newTime}
                  className="w-full px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 disabled:opacity-50"
                  data-testid="reschedule-submit"
                >
                  {submittingResched ? 'Submitting...' : 'Confirm Reschedule'}
                </button>
              </form>
            )}

            <div className="text-center text-xs text-gray-500 pt-4 border-t border-gray-100">
              Need help? Contact us at{' '}
              <a href="mailto:media@matthewminchio.com" className="text-teal-700 underline">
                media@matthewminchio.com
              </a>{' '}
              or call <a href="tel:+61431323137" className="text-teal-700 underline">+61 431 323 137</a>.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <dt className="text-gray-500">{label}</dt>
    <dd className="text-gray-900 font-medium text-right">{value}</dd>
  </div>
);

const ResultCard = ({ title, body, testid }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
    <div className="bg-white rounded-2xl shadow-md p-10 max-w-lg w-full text-center" data-testid={testid}>
      <div className="w-14 h-14 bg-teal-100 rounded-full mx-auto mb-5 flex items-center justify-center">
        <svg className="w-7 h-7 text-teal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-3">{title}</h1>
      <p className="text-gray-600 mb-6 leading-relaxed">{body}</p>
      <Link to="/" className="inline-block bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700">
        Return Home
      </Link>
    </div>
  </div>
);
